import { useEffect, useState } from "react";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Kicker, Reveal } from "./Reveal";
import { getConsent } from "./CookieConsent";
import { CONTACT, LEISTUNG_OPTIONS } from "../data/content";

const MAP_SRC = `https://maps.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

const initialForm = { name: "", phone: "", email: "", ort: "", leistung: "", nachricht: "", rueckruf: false, datenschutz: false };

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [loadMap, setLoadMap] = useState(() => getConsent()?.media === true);

  useEffect(() => {
    const onConsent = (e) => {
      if (e.detail?.media) setLoadMap(true);
    };
    window.addEventListener("berglar:consent-changed", onConsent);
    return () => window.removeEventListener("berglar:consent-changed", onConsent);
  }, []);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, leistung: e.detail }));
    window.addEventListener("berglar:prefill", onPrefill);
    const stored = sessionStorage.getItem("berglar:prefill");
    if (stored) {
      setForm((f) => ({ ...f, leistung: stored }));
      sessionStorage.removeItem("berglar:prefill");
    }
    return () => window.removeEventListener("berglar:prefill", onPrefill);
  }, []);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/anfrage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          telefon: form.phone,
          email: form.email,
          ort: form.ort,
          leistung: form.leistung,
          nachricht: form.nachricht,
          rueckruf: form.rueckruf,
        }),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success("Vielen Dank für Ihre Anfrage.", {
        description: "Wir melden uns in der Regel innerhalb von zwei Werktagen persönlich bei Ihnen.",
      });
      setForm(initialForm);
    } catch {
      toast.error("Senden leider fehlgeschlagen.", {
        description: "Bitte versuchen Sie es erneut – oder rufen Sie uns direkt an: 0 27 61 / 6 50 64.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="relative overflow-hidden py-24 md:py-32 bg-white" data-testid="contact-section">
      <img
        src="/images/wappen.png"
        alt=""
        aria-hidden="true"
        className="absolute -right-16 top-1/2 -translate-y-1/2 h-[560px] w-auto opacity-[0.05] pointer-events-none select-none hidden md:block"
      />
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>Kontakt</Kicker>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 data-testid="contact-title" className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] mt-4 text-ink">
                Erzählen Sie uns von Ihrem Vorhaben – <span className="font-fraktur text-golddark">wir hören zu.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="text-muted leading-relaxed mt-7 max-w-md">
                Ob privat, gewerblich oder kommunal: Eine kurze Beschreibung genügt – wir melden uns persönlich.
                Am schnellsten geht es per Anruf:
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <a
                href={CONTACT.phoneHref}
                data-testid="contact-phone-link"
                className="block font-mono text-[clamp(1.6rem,3vw,2.4rem)] font-bold tracking-tight text-ink hover:text-golddark transition-colors duration-300 mt-8 py-1"
              >
                {CONTACT.phoneDisplay}
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="inline-flex items-center gap-2 text-muted hover:text-golddark transition-colors duration-300 mt-3 py-2"
              >
                <Mail size={16} className="text-golddark" /> {CONTACT.email}
              </a>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="border-t border-line mt-10 pt-8">
                <p className="kicker !text-[11px]">Direkt erreichen</p>
                <p className="font-display text-2xl font-semibold tracking-tight mt-3 text-ink" data-testid="contact-owner">{CONTACT.owner}</p>
                <p className="flex items-start gap-3 text-muted mt-4">
                  <MapPin size={18} className="text-golddark mt-0.5 shrink-0" />
                  <span>{CONTACT.street}<br />{CONTACT.city}</span>
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-golddark mt-5">
                  Einsatzgebiet: Olpe · Wenden · Südsauerland
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.34}>
              <div className="relative aspect-[16/10] border border-line rounded-xl overflow-hidden mt-10" data-testid="map-container">
                {loadMap ? (
                  <iframe
                    title="Anfahrt – Manfred Berglar, Malermeister, Höhweg 14, 57462 Olpe-Griesemert"
                    src={MAP_SRC}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                ) : (
                  <div className="absolute inset-0 bg-silver text-ink flex flex-col items-center justify-center gap-5 p-8 text-center">
                    <MapPin size={30} className="text-golddark" strokeWidth={1.5} />
                    <p className="text-muted text-sm leading-relaxed max-w-sm">
                      Aus Datenschutzgründen wird die Karte erst nach Ihrem Klick geladen. Dabei werden Daten an Google
                      Maps übertragen.
                    </p>
                    <button onClick={() => setLoadMap(true)} data-testid="load-map-button" className="btn-primary">
                      Karte laden
                    </button>
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="border border-line border-t-[3px] border-t-gold bg-surface rounded-2xl p-8 md:p-12 flex flex-col gap-7">
              <p className="kicker !text-[11px]">Anfrage stellen</p>
              <div className="grid sm:grid-cols-2 gap-7">
                <div>
                  <label htmlFor="name" className="kicker !text-[11px] block mb-1">Name *</label>
                  <input id="name" data-testid="contact-input-name" required value={form.name} onChange={set("name")} className="input-line" placeholder="Ihr Name" />
                </div>
                <div>
                  <label htmlFor="phone" className="kicker !text-[11px] block mb-1">Telefonnummer</label>
                  <input id="phone" data-testid="contact-input-phone" type="tel" value={form.phone} onChange={set("phone")} className="input-line" placeholder="Für Rückfragen" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <div>
                  <label htmlFor="email" className="kicker !text-[11px] block mb-1">E-Mail *</label>
                  <input id="email" data-testid="contact-input-email" type="email" required value={form.email} onChange={set("email")} className="input-line" placeholder="ihre@mail.de" />
                </div>
                <div>
                  <label htmlFor="ort" className="kicker !text-[11px] block mb-1">Ort / Gemeinde</label>
                  <input id="ort" data-testid="contact-input-ort" value={form.ort} onChange={set("ort")} className="input-line" placeholder="z. B. Olpe" />
                </div>
              </div>
              <div>
                <label htmlFor="leistung" className="kicker !text-[11px] block mb-1">Gewünschte Leistung</label>
                <Select value={form.leistung} onValueChange={(v) => setForm((f) => ({ ...f, leistung: v }))}>
                  <SelectTrigger
                    id="leistung"
                    data-testid="contact-select-leistung"
                    className="w-full h-auto rounded-none border-0 border-b-2 border-line bg-transparent px-0 py-3 text-base text-ink shadow-none focus:ring-0 focus:border-gold data-[placeholder]:text-muted/60"
                  >
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-line bg-white text-ink">
                    {LEISTUNG_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o} className="rounded-lg focus:bg-surface focus:text-ink" data-testid={`contact-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="nachricht" className="kicker !text-[11px] block mb-1">Nachricht *</label>
                <textarea id="nachricht" data-testid="contact-input-message" required rows={4} value={form.nachricht} onChange={set("nachricht")} className="input-line resize-none" placeholder="Worum geht es? Kurze Beschreibung genügt – Ausschreibungsunterlagen können Sie gern erwähnen, wir melden uns dann zur Übermittlung." />
              </div>
              <label className="flex items-start gap-3 cursor-pointer py-2 -my-1">
                <input
                  type="checkbox"
                  checked={form.rueckruf}
                  onChange={set("rueckruf")}
                  data-testid="contact-callback-checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-[#8A6D43] cursor-pointer"
                />
                <span className="text-sm text-muted leading-relaxed">
                  Bitte um <span className="font-semibold text-ink">Rückruf</span> – ich wünsche telefonische Beratung.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer py-2 -my-1" data-testid="contact-privacy-label">
                <input
                  type="checkbox"
                  required
                  checked={form.datenschutz}
                  onChange={set("datenschutz")}
                  data-testid="contact-privacy-checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-[#8A6D43] cursor-pointer"
                />
                <span className="text-sm text-muted leading-relaxed">
                  Ich habe die <a href="/datenschutz" className="underline underline-offset-2 hover:text-golddark transition-colors" data-testid="contact-privacy-link">Datenschutzerklärung</a> gelesen
                  und bin mit der Verarbeitung meiner Daten zur Bearbeitung der Anfrage einverstanden. *
                </span>
              </label>
              <button type="submit" disabled={sending} data-testid="contact-submit-button" className="btn-primary self-start disabled:opacity-60">
                {sending ? "Wird gesendet …" : "Projekt anfragen"} <ArrowUpRight size={16} />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
